import { useEffect, useRef } from 'react';

import { useLiveLessonClickerContext } from '../providers/LiveLessonClickerProvider';
import {
  decideNextAction,
  executeNextAction,
  STATE_MUTATING_NEXT_ACTIONS,
} from './liveLessonNextAction';

const ADVANCE_KEYS: ReadonlySet<string> = new Set([
  'PageDown',
  ' ',
  'Spacebar',
  'Enter',
  'ArrowRight',
]);
const BACK_KEYS: ReadonlySet<string> = new Set(['PageUp', 'ArrowLeft']);

// Tab is the standard focus-nav key, so we only treat it as a clicker
// signal when pressed without Ctrl/Alt/Meta (those are reserved for
// browser/OS shortcuts like Ctrl+Tab). Plain Tab advances; Shift+Tab
// goes back — matching how Kensington and similar presenter remotes
// emit it in their non-PageUp/PageDown modes.
function classifyClickerKey(e: KeyboardEvent): 'advance' | 'back' | null {
  if (e.key === 'Tab') {
    if (e.ctrlKey || e.altKey || e.metaKey) return null;
    return e.shiftKey ? 'back' : 'advance';
  }
  if (ADVANCE_KEYS.has(e.key)) return 'advance';
  if (BACK_KEYS.has(e.key)) return 'back';
  return null;
}

function isTextInputFocused(): boolean {
  if (typeof document === 'undefined') return false;
  const el = document.activeElement as HTMLElement | null;
  if (!el) return false;
  const tag = el.tagName?.toLowerCase();
  if (tag === 'input' || tag === 'textarea' || tag === 'select') return true;
  if (el.isContentEditable) return true;
  // JSDOM's isContentEditable getter ignores the attribute, so fall back
  // to a direct attribute check (also covers contenteditable="").
  const attr = el.getAttribute?.('contenteditable');
  if (attr !== null && attr !== undefined && attr !== 'false') return true;
  return false;
}

export interface UseClickerControlsArgs {
  enabled: boolean;
  debounceMs?: number;
  onFirstUse?: () => void;
  // Test seam: lets tests inject a deterministic clock without faking
  // timers across the whole module.
  nowFn?: () => number;
}

// Listens for clicker keys (PageDown/PageUp, Space, Enter, ArrowRight/
// ArrowLeft, Tab/Shift+Tab) and routes them through the central
// decideNextAction → executeNextAction pipeline. Reads handlers and
// state from LiveLessonClickerProvider; do not call outside of that
// provider.
export function useClickerControls({
  enabled,
  debounceMs = 500,
  onFirstUse,
  nowFn,
}: UseClickerControlsArgs): void {
  const ctx = useLiveLessonClickerContext();

  // Sentinel value places the first press well outside any debounce
  // window so the very first clicker keystroke always fires.
  const lastFireAtRef = useRef(Number.NEGATIVE_INFINITY);
  const firstUseFiredRef = useRef(false);
  const onFirstUseRef = useRef(onFirstUse);
  useEffect(() => {
    onFirstUseRef.current = onFirstUse;
  }, [onFirstUse]);

  useEffect(() => {
    if (!enabled) return;
    if (!ctx) return;
    if (typeof window === 'undefined') return;

    const now = nowFn ?? (() => Date.now());

    const handler = (e: KeyboardEvent) => {
      const direction = classifyClickerKey(e);
      if (direction === null) return;
      const isAdvance = direction === 'advance';
      const isBack = direction === 'back';

      // Don't steal keystrokes from text editing surfaces.
      if (isTextInputFocused()) return;

      // Ignore auto-repeat from holding the key down.
      if (e.repeat) {
        e.preventDefault();
        return;
      }

      const sinceLast = now() - lastFireAtRef.current;

      if (isAdvance) {
        const action = decideNextAction(ctx.stateRef.current);

        if (action === 'noop') {
          // Consume the key so the page doesn't scroll.
          e.preventDefault();
          return;
        }

        // Drop a press inside the debounce window after a state-mutating
        // action — guards against a teacher's nervous double-tap firing
        // two transitions back-to-back.
        if (STATE_MUTATING_NEXT_ACTIONS.has(action) && sinceLast < debounceMs) {
          e.preventDefault();
          return;
        }

        e.preventDefault();
        lastFireAtRef.current = now();
        if (!firstUseFiredRef.current) {
          firstUseFiredRef.current = true;
          onFirstUseRef.current?.();
        }
        executeNextAction(action, ctx.handlersRef.current);
        return;
      }

      // Back
      if (sinceLast < debounceMs) {
        e.preventDefault();
        return;
      }
      e.preventDefault();
      lastFireAtRef.current = now();
      if (!firstUseFiredRef.current) {
        firstUseFiredRef.current = true;
        onFirstUseRef.current?.();
      }
      ctx.handlersRef.current.skipBack?.();
    };

    // Capture phase so preventDefault wins over the focused button's
    // synthesized click (e.g. when the end-lesson modal is open and
    // Cancel is focused, we still confirm — that's what we want).
    window.addEventListener('keydown', handler, true);
    return () => window.removeEventListener('keydown', handler, true);
  }, [enabled, debounceMs, ctx, nowFn]);
}
